/**
 * Layer 3 — CoreRuleEngine  (audited: BZ / Sheet27 layer)
 * ─────────────────────────────────────────────────────────────────────────────
 * Deterministic implementation of the validated Life Blueprint CORE rules.
 *
 * Contract:
 *   runCoreRuleEngine(normalizedInput, referenceData) -> CoreOutput
 *
 * CoreOutput is the INTERMEDIATE, machine-oriented result. It is NOT rendered
 * directly by the UI. The next layer (AnalysisEngine) consumes it.
 *
 * Rules for this file:
 *   - No randomness. Same (input, refData version) MUST yield identical output.
 *   - MOCK path lives only in `runCoreRuleEngineMock` below and merely surfaces
 *     a variant selector derived from `src/lib/mockProfiles`. It is NOT a
 *     Life Blueprint calculation.
 *   - FUTURE_V299_RULE: `runCoreRuleEngineV299` throws until the Golden Master
 *     rules are supplied. Do NOT implement here from memory or inference.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { SOURCE_STATUS, ENGINE_VERSION } from "./sourceStatus";
import { MOCK_VARIANTS } from "@/lib/mockProfiles";
import { runCoreRuleV299Production } from "./v299/productionCoreAdapter";

/**
 * @typedef {Object} CoreOutput
 * @property {"MOCK_POC"|"VALIDATED_V299"} source
 * @property {string} engineVersion
 * @property {string} inputHash           echoed from normalize()
 * @property {Object} pillars             validated core pillars/values (V2.9.9 shape TBD)
 * @property {Object} rawFactors          raw factor set required by AnalysisEngine
 * @property {?string} mockVariantId      only set in MOCK path
 */

const NOT_IMPLEMENTED_V299 =
  "[CoreRuleEngine · V2.9.9] Not implemented yet. This layer corresponds to the " +
  "audited BZ / Sheet27 calculation stage and must be migrated 1:1 from the " +
  "validated Golden Master. Do NOT invent rules.";

// ─────────────────────────── MOCK PATH (PoC only) ───────────────────────────

const SAMPLE_TO_VARIANT = {
  "张明|1990-05-12": 0,
  "李雨欣|1995-11-03": 1,
  "陈家豪|1985-02-27": 2,
};

function pickMockVariantIndex(normalized) {
  const key = `${normalized.name}|${normalized.birthDateISO}`;
  if (Object.prototype.hasOwnProperty.call(SAMPLE_TO_VARIANT, key)) {
    return SAMPLE_TO_VARIANT[key];
  }
  let h = 5381;
  for (let i = 0; i < normalized.rawKey.length; i += 1) {
    h = ((h << 5) + h + normalized.rawKey.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % MOCK_VARIANTS.length;
}

/**
 * MOCK core stage. Emits a `mockVariantId` used by the mock AnalysisEngine
 * to fetch its sample dataset. This is NOT a Life Blueprint calculation.
 *
 * @param {import('./schema').NormalizedInput} normalized
 * @param {typeof import('./referenceData').referenceData} _refData
 * @returns {CoreOutput}
 */
export function runCoreRuleEngineMock(normalized, _refData) {
  const idx = pickMockVariantIndex(normalized);
  const variant = MOCK_VARIANTS[idx];
  return {
    source: SOURCE_STATUS.MOCK_POC,
    engineVersion: ENGINE_VERSION.POC_MOCK,
    inputHash: normalized.rawKey,
    pillars: {},         // MOCK: not computed
    rawFactors: {},      // MOCK: not computed
    mockVariantId: variant.id,
  };
}

// ─────────────────────────── V1 TASK 1 · PRODUCTION-CONNECTED ──────────────

/**
 * Production V2.9.9 core rule entry point.
 *
 * V1 Task 1 (2026-02) replaces the historical NOT_IMPLEMENTED body with a
 * thin delegation to the audited production adapter at
 * `engine/v299/productionCoreAdapter.js`. The adapter is composed only
 * from ALREADY-VALIDATED V2.9.9 deterministic modules (M02 references,
 * M03 Sheet27 batches 01–07, Batch 07-2 Sheet27 B85:E88 closure, M04
 * minimum Sheet28). It does NOT import `poc_composer`, `validation/**`,
 * `devHooks`, or any GM fixture.
 *
 * Callers MUST attach `normalized.audited` — the audited Golden-Master
 * upstream cells (B5/B8/B13/C32/E19..E30/F20/F19..F30/B14). If missing,
 * the adapter fails closed with `UNSUPPORTED_PRODUCTION_INPUT`. The
 * M01 preprocessing that would produce those cells from raw birth
 * datetime is NOT MIGRATED — Task 1 must not fabricate it.
 *
 * Downstream `runAnalysisEngineV299` and `runInterpretationEngineV299`
 * are UNCHANGED and still throw `NOT_IMPLEMENTED`, so the full V2.9.9
 * `runPipeline` still throws at the analysis stage. This is
 * intentional — Task 1 is CORE ONLY. Task 2 is not authorized.
 *
 * @param {import('./schema').NormalizedInput & { audited?: object }} normalized
 * @param {typeof import('./referenceData').referenceData} referenceData
 * @returns {CoreOutput}
 */
export function runCoreRuleEngineV299(normalized, referenceData) {
  return runCoreRuleV299Production(normalized, referenceData);
}
